import {comp, cookies, root, Router, router, tbar} from "@intermesh/goui";
import {PortalConfig} from "./config.js";
import {CurrentSection} from "./services/CurrentSection.js";
import {CurrentUser} from "./services/CurrentUser.js";
import {ServerApi} from "./util/ServerApi.js";
import {LanguageSelect} from "./LanguageSelect.js";
import {CheckAuth} from "./CheckAuth.js";
import {Login} from "./Login.js";
import {Profile} from "./Profile.js";
import {Welcome} from "./Welcome.js";
import {Register} from "./Register.js";
import {PasswordReset} from "./PasswordReset.js";
import {NotFound} from "./NotFound.js";
import {ConfirmRegistration} from "./ConfirmRegistration.js";
import {SectionInfo} from "./SectionInfo.js";
import {CourseSelect} from "./CourseSelect.js";
import {ParticipantEdit} from "./ParticipantEdit.js";
import {Course} from "./Course.js";
import {CourseStatus} from "./CourseStatus.js";
import {SectionSelect} from "./SectionSelect.js";
import {ScormCourseSelect} from "./ScormCourseSelect.js";
import {UnsubscribeReminders} from "./UnsubscribeReminders.js";
import {translate} from "./util/Translate.js";
import {ConfirmParticipantEdit} from "./ConfirmParticipantEdit.js";
import "./components/ErrorLogger.js";

let p = [];

if (!localStorage.getItem("portalConfig") || cookies.get("accessToken") || location.href.match(/\/[A-Z]{2}$/)) {
	p.push(CurrentUser.readIf());
} else if (!location.href.match(/#!\/checkauth\?/)) {
	// Dirty hack: with old-style invitation URLs we do NOT want to log out.
	CurrentUser.logout();
}
// Catch URL parameters, put them into session variables
if (document.location.search) {
	const searchParams = new URLSearchParams(document.location.search);
	searchParams.forEach((value, key) => {
		sessionStorage.setItem(key, value);
	});
}
p.push(CurrentSection.readIf());
Promise.all(p).then((_response) => {
	// Fall back on default section IDs for logo and background
	let frontendLogoSectionId = 1, frontendBackgroundSectionId = 1;
	if (localStorage.getItem("portalConfig")) {
		const pc = JSON.parse(localStorage.getItem("portalConfig")!);
		frontendBackgroundSectionId = pc.defaultSectionId;
		frontendLogoSectionId = pc.defaultSectionId;
	}
	const cs = CurrentSection.get();
	if (cs && cs.background) {
		frontendBackgroundSectionId = cs.id;
	}
	if (cs && cs.frontendLogo) {
		frontendLogoSectionId = cs.id;
	}

	const elCmp = comp(
		{
			id: "el-container",
			cls: "el-container"
		}
	);

	const logoCmp = comp({
			tagName: "div",
			cls: "go-logo layout-align-start-center layout row"
		}, comp({
			tagName: "img",
			id: 'ifw-logo',
			attr: {
				src: ServerApi.url('elearning/frontend/sections/' + frontendLogoSectionId + '/logo', {}, true),
				alt: PortalConfig.title
			}
		})
	);

	document.body.style.backgroundImage = "url(" + ServerApi.url('elearning/frontend/sections/' + frontendBackgroundSectionId + '/background', {}, true) + ")";
	document.head.insertAdjacentHTML("beforeend", `<link type="text/css" rel="stylesheet" href="${PortalConfig.APIUrl}/webclient/custom.css">`);
	document.title = PortalConfig.title;

	root.items.add(
		comp({
			tagName: "div",
			cls: "go-progress-container"
		}),
		tbar({
				cls: "go-header"
			},
			logoCmp),
		comp({
				id: "go-container"
			},
			elCmp
		));
	const r = new Router();
	// setup and start router
	r.add(/^$/, () => {
		// Exception #59385038 : if there is an access token and SSO is used, move directly to profile
		const pc = JSON.parse(localStorage.getItem("portalConfig")!);
		if (cookies.get("accessToken") && pc.mayUseOauth) {
			void r.goto("profile");
		} else {
			const ls = new LanguageSelect();
			elCmp.items.replace(ls);
		}
	}).add(/^clear$/, () => {
		sessionStorage.clear();
		void r.goto("");
	}).add(/^login$/, () => {
		const l = new Login();
		elCmp.items.replace(l);
		return l;
	}).add(/^check-auth$/, () => {
		const ca = new CheckAuth();
		elCmp.items.replace(ca);
	}).add(/^welcome$/, () => {
		const l = new Welcome();
		elCmp.items.replace(l);
	}).add(/^profile$/, () => {
		const l = new Profile();
		elCmp.items.replace(l);
	}).add(/^participant-edit$/, () => {
		const l = new ParticipantEdit();
		elCmp.items.replace(l);
	}).add(/^confirm-participant-edit$/, () => {
		const l = new ConfirmParticipantEdit();
		elCmp.items.replace(l);
	}).add(/^register$/, () => {
		const l = new Register();
		elCmp.items.replace(l);
	}).add(/^confirm-registration$/, () => {
		const l = new ConfirmRegistration();
		elCmp.items.replace(l);
	}).add(/^passwordReset\/([a-z0-9]+)?\/(\d+)\/([A-Z]{2})$/, async (token: string, userId: string, language: string) => {
		sessionStorage.setItem("language", language);
		await translate.load();
		const l = new PasswordReset(token, userId);
		elCmp.items.replace(l);
	}).add(/^section-info$/, () => {
		const l = new SectionInfo();
		elCmp.items.replace(l);
	}).add(/^section-selection$/, () => {
		const l = new SectionSelect();
		elCmp.items.replace(l);

	}).add(/^course\/(\d+)?\/(\d+)?$/, (courseId: string, blockId: string) => {
		const course = new Course(courseId, blockId);
		elCmp.items.replace(course);

	}).add(/^course-selection$/, () => {
		const courseSelect = new CourseSelect();
		elCmp.items.replace(courseSelect);
	}).add(/^scorm-course-selection$/, () => {
		const courseSelect = new ScormCourseSelect();
		elCmp.items.replace(courseSelect);
	}).add(/^course-status$/, () => {
		const cs = new CourseStatus();
		elCmp.items.replace(cs);
	}).add(/^([A-Z]{2})\/unsubscribe-reminders\/(\d+)$/, async (language: string, participantId: string) => {
		sessionStorage.setItem("language", language);
		await translate.load();
		if (!cookies.get("accessToken")) {
			sessionStorage.setItem("routeBeforeAuth", language + "/unsubscribe-reminders/" + participantId);
			void router.goto("login");
		} else {
			const l = new UnsubscribeReminders(participantId);
			elCmp.items.replace(l);
		}
	}).add(() => {
		/*
		  Dirty hackish solution: parse very non-standard Angular style invitation token, save token and move on.

		  Old style URL:
			https://host.docker.internal:7443/entrance/#!/checkauth?invitationToken=88483-36-6130323832303930626566633035373566663934323637366431353539656237
		  New style URL:
		    https://host.docker.internal:7443/entrance/?invitationToken=88483-36-6130323832303930626566633035373566663934323637366431353539656237
		 */
		if (location.href.match(/#!\/checkauth\?/)) {
			const arUrl = location.href.split("?");
			if (arUrl[1]) {
				const searchParams = new URLSearchParams(arUrl[1]);
				searchParams.forEach((value, key) => {
					sessionStorage.setItem(key, value);
				});
				elCmp.items.replace(new LanguageSelect());
				return;
			}
		}
		const l = new NotFound(); // 404
		elCmp.items.replace(l);
	})
		.start();
});